import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { onSessionExpired, refreshTokens } from "@/api/client";
import { authApi, usersApi } from "@/api/endpoints";
import { accessTtlMs, onTokensChange, readTokens, writeTokens, type TokenPair } from "@/api/tokens";
import type { Me } from "@/api/types";
import { getLiveStore } from "@/live/store";

interface AuthState {
  tokens: TokenPair | null;
  me: Me | undefined;
  meError: unknown;
  meLoading: boolean;
  can: (perm: string) => boolean;
  canAny: (...perms: string[]) => boolean;
  signIn: (pair: TokenPair) => void;
  signOut: () => Promise<void>;
  /** Set when the session ended on its own (refresh failed), for the login page. */
  expired: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const REFRESH_LEAD_MS = 60_000;
const EXPIRED_KEY = "omf.sessionExpired";

// The "session expired" notice must survive the redirect/reload to /login.
function rememberExpired(v: boolean): void {
  try {
    if (v) sessionStorage.setItem(EXPIRED_KEY, "1");
    else sessionStorage.removeItem(EXPIRED_KEY);
  } catch {
    // notice just won't survive a reload
  }
}

function wasExpired(): boolean {
  try {
    return sessionStorage.getItem(EXPIRED_KEY) === "1";
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [tokens, setTokens] = useState<TokenPair | null>(() => readTokens());
  const [expired, setExpired] = useState(wasExpired);

  useEffect(() => onTokensChange(() => setTokens(readTokens())), []);

  useEffect(() => {
    onSessionExpired(() => {
      setExpired(true);
      rememberExpired(true);
      getLiveStore().reset();
      queryClient.clear();
    });
    return () => onSessionExpired(null);
  }, [queryClient]);

  // Proactive refresh shortly before the access token expires, so streams
  // and requests don't all hit a 401 at once. Every tab schedules this; the
  // Web Lock in refreshTokens() lets only one actually rotate the pair.
  const accessToken = tokens?.access_token;
  useEffect(() => {
    const t = readTokens();
    if (!t) return;
    const delay = Math.max(accessTtlMs(t) - REFRESH_LEAD_MS, 5_000);
    const timer = setTimeout(() => {
      const held = readTokens();
      if (held) void refreshTokens(held).catch(() => undefined);
    }, delay);
    return () => clearTimeout(timer);
  }, [accessToken]);

  const meQuery = useQuery({
    queryKey: ["me", tokens ? "signed-in" : "anon"],
    queryFn: usersApi.me,
    enabled: tokens !== null,
    staleTime: 60_000,
    retry: 1,
  });

  // The live streams run for as long as someone is signed in.
  const signedIn = tokens !== null;
  useEffect(() => {
    if (!signedIn) return;
    const live = getLiveStore();
    live.start();
    return () => live.stop();
  }, [signedIn]);

  const me = meQuery.data;
  const can = useCallback(
    (perm: string) => Boolean(me && (me.is_admin_bypass || me.effective_permissions.includes(perm))),
    [me],
  );
  const canAny = useCallback((...perms: string[]) => perms.some(can), [can]);

  const signIn = useCallback(
    (pair: TokenPair) => {
      setExpired(false);
      rememberExpired(false);
      writeTokens(pair);
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    const held = readTokens();
    setExpired(false);
    rememberExpired(false);
    getLiveStore().reset();
    writeTokens(null);
    queryClient.clear();
    if (held) await authApi.logout(held.refresh_token).catch(() => undefined);
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      tokens,
      me,
      meError: meQuery.error,
      meLoading: meQuery.isLoading,
      can,
      canAny,
      signIn,
      signOut,
      expired,
    }),
    [tokens, me, meQuery.error, meQuery.isLoading, can, canAny, signIn, signOut, expired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

export function useCan(perm: string): boolean {
  return useAuth().can(perm);
}
